import * as React from 'react';
import { type StyleProp, Text, type TextStyle } from 'react-native';

import type * as api from '@gitbook/api-client';
import { themedFonts, useCurrentTheme } from '@gitbook/react-uikit';

import type { ContentKitElementProps } from './types';

export const ContentKitTextStyleContext = React.createContext<StyleProp<TextStyle>>(null);
const CascadingStylesContext = React.createContext<{ cascading: boolean }>({ cascading: false });

export function ContentKitText(
    props: ContentKitElementProps<api.ContentKitText>
): React.ReactElement {
    const { element, renderElements } = props;
    const [, font] = useCurrentTheme(themedFonts);
    const renderContextStyle = React.useContext(ContentKitTextStyleContext);
    const { cascading } = React.useContext(CascadingStylesContext);

    let elementStyle: StyleProp<TextStyle>;
    switch (element.style) {
        case 'bold':
            elementStyle = font.Bold;
            break;
        case 'code':
            elementStyle = font.Code;
            break;
        case 'italic':
            elementStyle = font.Italic;
            break;
        case 'strikethrough':
            elementStyle = font.Strikethrough;
            break;
    }

    /**
     * The text styles need to be applied only once at the root or first text element.
     * After that we will leverage the cascading property to prevent applying the styles again
     * which can cause unexpected behavior like overriding the `elementStyle`
     */
    const textStyles = cascading
        ? []
        : renderContextStyle
          ? [renderContextStyle]
          : [font.text, font.Body];

    const textComponent = (
        <Text selectable style={[...textStyles, elementStyle]}>
            {typeof element.children === 'string'
                ? element.children
                : renderElements('inline', element.children)}
        </Text>
    );

    /**
     * If there is no renderContextStyle or cascading is false, we need to wrap the text component
     * with the context providers to ensure that the text styles can be cascaded down to the children
     */
    if (!renderContextStyle || !cascading) {
        return (
            <ContentKitTextStyleContext.Provider value={textStyles}>
                <CascadingStylesContext.Provider value={{ cascading: true }}>
                    {textComponent}
                </CascadingStylesContext.Provider>
            </ContentKitTextStyleContext.Provider>
        );
    }

    return textComponent;
}
